import { useEffect, useState } from "react";
import moment from "moment";
import "chart.js/auto";
import { Bar } from "react-chartjs-2";
import { CustomDialog } from "@/components/dialog/v1/CustomDialog";
import { useRefreshShortLinkList, useShortLinkStatsQuery } from "@/hooks/ReactQuery/ShortLinkReactQuery/ShortLinkReactQuery";

const DAY_MS = 24 * 60 * 60 * 1000;
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const PERIODS = [7, 30, 90];

// 차트 막대 색: 브랜드 핑크(#ee9ca7)는 흰 배경 대비 2:1 로 막대가 흐려서 같은 계열의 진한 핑크를 쓴다 (3:1 이상)
const BAR_COLOR = '#d9577a';

const IN_APP_LABELS = {
    instagram: '인스타그램',
    kakaotalk: '카카오톡',
    facebook: '페이스북',
    naver: '네이버 앱',
    line: '라인',
};
const DEVICE_LABELS = { mobile: '모바일', desktop: 'PC' };

const regionNames = new Intl.DisplayNames(['ko'], { type: 'region' });

const BREAKDOWNS = [
    { key: 'source', title: '채널', hint: '단축 URL 의 ?src= (없으면 utm_source)', label: (k) => k ?? '지정 안 함' },
    { key: 'inApp', title: '열린 앱', hint: '어느 앱 안에서 눌렀는지', label: (k) => IN_APP_LABELS[k] || (k ?? '일반 브라우저') },
    { key: 'referer', title: '유입 사이트', hint: '앱 안에서는 비어 있는 경우가 많습니다', label: (k) => k ?? '알 수 없음' },
    { key: 'device', title: '기기', label: (k) => DEVICE_LABELS[k] || (k ?? '알 수 없음') },
    { key: 'country', title: '국가', label: (k) => (k ? regionNames.of(k) : '알 수 없음') },
];

// KST 기준 오늘부터 days 일 전까지
function getRange(days) {
    const today = new Date(Date.now() + KST_OFFSET_MS);
    const from = new Date(today.getTime() - (days - 1) * DAY_MS);
    return { from: from.toISOString().slice(0, 10), to: today.toISOString().slice(0, 10) };
}

function formatDate(date) {
    return `${date.slice(5, 7)}.${date.slice(8, 10)}`;
}

function StatTile({ label, value, hint }) {
    return (
        <div className="rounded-xl bg-gray-50 px-4 py-3">
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-2xl font-bold mt-0.5">{value}</p>
            {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
        </div>
    );
}

function Breakdown({ title, hint, rows, label, total }) {
    return (
        <div className="flex flex-col gap-2">
            <div>
                <p className="text-sm font-bold">{title}</p>
                {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
            </div>
            {rows.length === 0 && <p className="text-xs text-gray-400">데이터 없음</p>}
            <ul className="flex flex-col gap-1.5">
                {rows.map(row => {
                    const ratio = total ? row.clicks / total : 0;
                    return (
                        <li key={row.key ?? '__null'} className="flex flex-col gap-1">
                            <div className="flex justify-between gap-2 text-xs">
                                <span className="truncate text-gray-700">{label(row.key)}</span>
                                <span className="shrink-0 text-gray-500"><b className="text-gray-800">{row.clicks.toLocaleString()}</b> · {Math.round(ratio * 100)}%</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-gray-100">
                                <div className="h-full rounded-full" style={{ width: `${Math.max(ratio * 100, 2)}%`, background: BAR_COLOR }} />
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export function MdShortLinkStats({ shortLink, onClose }) {
    const [days, setDays] = useState(30);
    const range = getRange(days);
    const statsQuery = useShortLinkStatsQuery({ id: shortLink?.id, ...range });
    const stats = statsQuery?.data;
    const refreshShortLinkList = useRefreshShortLinkList();

    // 통계를 열면 목록의 클릭 수도 최신으로 맞춘다
    useEffect(() => {
        if (shortLink?.id) {
            refreshShortLinkList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shortLink?.id]);

    const handleRefresh = () => {
        statsQuery.refetch();
        refreshShortLinkList();
    }

    const chartData = stats && {
        labels: stats.daily.map(d => formatDate(d.date)),
        datasets: [{
            label: '클릭',
            data: stats.daily.map(d => d.clicks),
            backgroundColor: BAR_COLOR,
            borderRadius: 4,
            borderSkipped: 'start',
            maxBarThickness: 24,
        }],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                displayColors: false,
                callbacks: {
                    title: (items) => stats.daily[items[0].dataIndex].date,
                    label: (item) => {
                        const d = stats.daily[item.dataIndex];
                        return `클릭 ${d.clicks.toLocaleString()} · 방문자 ${d.visitors.toLocaleString()}`;
                    },
                },
            },
        },
        scales: {
            x: { grid: { display: false }, ticks: { color: '#6b7280', maxTicksLimit: 8, maxRotation: 0 } },
            y: { beginAtZero: true, grid: { color: '#f1f1f1', drawBorder: false }, ticks: { color: '#6b7280', precision: 0, maxTicksLimit: 5 } },
        },
    };

    return (
        <CustomDialog open={!!shortLink} onClose={onClose} maxWidth="md">
            <CustomDialog.CloseButton onClose={onClose} />
            {shortLink &&
                <div className="px-6 pb-6 flex flex-col gap-5">
                    <div className="flex flex-wrap items-end justify-between gap-3">
                        <div className="min-w-0">
                            <h3 className="text-lg font-bold break-all">{shortLink.title || shortLink.shortUrl.replace(/^https?:\/\//, '')} 클릭 통계</h3>
                            <p className="text-xs text-gray-500 mt-0.5 truncate">{shortLink.title && `${shortLink.shortUrl} · `}{shortLink.url}</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={handleRefresh}
                                disabled={statsQuery?.isFetching}
                                aria-label="새로고침"
                                title={statsQuery?.dataUpdatedAt ? `${moment(statsQuery.dataUpdatedAt).format('HH:mm:ss')} 기준 · 새로고침` : '새로고침'}
                                className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
                            >
                                <span className={`inline-block ${statsQuery?.isFetching ? 'animate-spin' : ''}`} aria-hidden="true">↻</span>
                                {statsQuery?.dataUpdatedAt ? moment(statsQuery.dataUpdatedAt).format('HH:mm') : ''}
                            </button>
                            <div className="flex rounded-lg border border-gray-200 p-0.5" role="group" aria-label="기간">
                                {PERIODS.map(p => (
                                    <button
                                        key={p}
                                        type="button"
                                        aria-pressed={days === p}
                                        onClick={() => setDays(p)}
                                        className={`rounded-md px-3 py-1.5 text-xs font-semibold ${days === p ? 'bg-gray-900 text-white' : 'text-gray-600'}`}
                                    >
                                        {p}일
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {statsQuery?.isLoading && <div className="h-[360px] rounded-xl bg-gray-50 animate-pulse" aria-busy="true" />}

                    {stats &&
                        <>
                            <div className="grid grid-cols-3 gap-2">
                                <StatTile label="클릭" value={stats.total.clicks.toLocaleString()} hint={`${formatDate(stats.from)} ~ ${formatDate(stats.to)}`} />
                                <StatTile label="방문자" value={stats.total.visitors.toLocaleString()} hint="IP 기준 추정" />
                                <StatTile label="일평균 클릭" value={(stats.total.clicks / stats.daily.length).toFixed(1)} />
                            </div>

                            <div>
                                <p className="text-sm font-bold mb-2">일별 클릭</p>
                                <div className="h-[200px]">
                                    <Bar data={chartData} options={chartOptions} aria-label="일별 클릭 막대 차트" role="img" />
                                </div>
                                <details className="mt-2 text-xs text-gray-500">
                                    <summary className="cursor-pointer">표로 보기</summary>
                                    <table className="mt-2 w-full text-left">
                                        <thead><tr className="text-gray-400"><th className="py-1 font-normal">날짜</th><th className="font-normal text-right">클릭</th><th className="font-normal text-right">방문자</th></tr></thead>
                                        <tbody>
                                            {stats.daily.slice().reverse().map(d => (
                                                <tr key={d.date} className="border-t border-gray-100 text-gray-700">
                                                    <td className="py-1">{d.date}</td>
                                                    <td className="text-right">{d.clicks.toLocaleString()}</td>
                                                    <td className="text-right">{d.visitors.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </details>
                            </div>

                            <div className="grid grid-cols-1 min-[600px]:grid-cols-2 gap-x-8 gap-y-5">
                                {BREAKDOWNS.map(b => (
                                    <Breakdown key={b.key} title={b.title} hint={b.hint} rows={stats[b.key]} label={b.label} total={stats.total.clicks} />
                                ))}
                            </div>

                            <p className="text-[11px] text-gray-400">
                                링크 미리보기·검색 봇 클릭 {stats.total.botClicks.toLocaleString()}회는 제외했습니다. 날짜는 한국 시간 기준이며, 항목별 상위 10개만 표시합니다.
                            </p>
                        </>
                    }
                </div>
            }
        </CustomDialog>
    );
}
